import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  FormControl, 
  FormControlLabel, 
  Switch,
  IconButton, 
  Tooltip,
  Button,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListSubheader,
  Alert,
  Stack,
  Checkbox,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import { AUDIENCE_FUNNEL_UI_ENABLED } from '../audience-funnel/featureFlag';
import {
  buildDeadlinePresets,
  formatDeadlineLabel,
  getEventDatesSummary,
  getRecommendedDeadline,
  getLotScheduleWarning,
  isValidRegistrationDeadline,
  type DeadlinePresetId,
} from './registrationDeadlineUtils';
import {
  getCatalogTicketsForFieldScope,
  getCustomFieldScopeLabel,
  expandTicketScopeIds,
  normalizeCustomFieldScope,
  isScopeOptionSelected,
  type TicketScopeOption,
} from '../../utils/customFieldTicketScope';
import type { EventFormData } from './EventFormContainer';
import { useBrand } from '../../brands/BrandProvider';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

interface RegistrationFormProps {
  /** true no painel do workspace — oculta título duplicado */
  embedded?: boolean;
}

// Tipos de campos personalizados organizados por categoria
const fieldTypesByCategory = [
  {
    category: 'Documentos',
    types: [
      { value: 'cpfCnpj', label: 'CPF/CNPJ' },
    ]
  },
  {
    category: 'Pessoais',
    types: [
      { value: 'idade', label: 'Idade' },
      { value: 'dataNascimento', label: 'Data de Nascimento' },
    ]
  },
  {
    category: 'Endereço',
    types: [
      { value: 'cep', label: 'CEP' },
    ]
  },
  {
    category: 'Geral',
    types: [
      { value: 'text', label: 'Texto' },
      { value: 'number', label: 'Número' },
      { value: 'select', label: 'Seleção' },
      { value: 'checkbox', label: 'Caixa de Seleção' },
      { value: 'label', label: 'Label (Texto Clicável)' }
    ]
  }
];

// Lista plana de todos os tipos (para compatibilidade)
const fieldTypes = fieldTypesByCategory.flatMap(cat => cat.types);

// Interface para os campos personalizados
interface CustomField {
  id: string;
  fieldName: string;
  fieldType: 'text' | 'number' | 'select' | 'checkbox' | 'label' | 'cpfCnpj' | 'idade' | 'cep' | 'dataNascimento';
  isRequired: boolean;
  options?: string[];
  appliesToAllTickets?: boolean;
  ticketScopeIds?: string[];
  useForSegmentation?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ embedded = false }) => {
  const { control, watch, setValue, formState: { errors } } = useFormContext<EventFormData>();
  const [openFieldDialog, setOpenFieldDialog] = useState(false);
  const [deadlineTouched, setDeadlineTouched] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<DeadlinePresetId | null>(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState<number | null>(null);
  const [fieldOptions, setFieldOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [tempFieldData, setTempFieldData] = useState({
    fieldName: '',
    fieldType: 'text' as 'text' | 'number' | 'select' | 'checkbox' | 'label' | 'cpfCnpj' | 'idade' | 'cep' | 'dataNascimento',
    isRequired: false,
    appliesToAllTickets: true,
    ticketScopeIds: [] as string[],
    useForSegmentation: false,
  });
  
  // Usar useFieldArray para gerenciar campos personalizados
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "customFields"
  });
  
  const handleOpenFieldDialog = (index?: number) => {
    if (index !== undefined) {
      setCurrentFieldIndex(index);
      // Acesso seguro às propriedades do campo
      const field = fields[index] as unknown as CustomField;
      const normalized = normalizeCustomFieldScope(field);
      setTempFieldData({
        fieldName: field.fieldName || '',
        fieldType: field.fieldType || 'text',
        isRequired: field.isRequired || false,
        appliesToAllTickets: normalized.appliesToAllTickets !== false,
        ticketScopeIds: normalized.ticketScopeIds || [],
        useForSegmentation: field.useForSegmentation === true,
      });
      setFieldOptions(field.options || []);
    } else {
      setCurrentFieldIndex(null);
      setTempFieldData({
        fieldName: '',
        fieldType: 'text',
        isRequired: false,
        appliesToAllTickets: true,
        ticketScopeIds: [],
        useForSegmentation: false,
      });
      setFieldOptions([]);
    }
    setOpenFieldDialog(true);
  };
  
  const handleCloseFieldDialog = () => {
    setOpenFieldDialog(false);
    setNewOption('');
  };
  
  const handleAddOption = () => {
    if (newOption.trim()) {
      setFieldOptions([...fieldOptions, newOption.trim()]);
      setNewOption('');
    }
  };
  
  const handleRemoveOption = (index: number) => {
    const newOptions = [...fieldOptions];
    newOptions.splice(index, 1);
    setFieldOptions(newOptions);
  };
  
  const handleSaveField = () => {
    const scopedIds = tempFieldData.appliesToAllTickets
      ? []
      : expandTicketScopeIds(tempFieldData.ticketScopeIds, catalogTicketOptions);

    const fieldData = {
      fieldName: tempFieldData.fieldName,
      fieldType: tempFieldData.fieldType,
      isRequired: tempFieldData.isRequired,
      appliesToAllTickets: scopedIds.length > 0 ? false : tempFieldData.appliesToAllTickets,
      ticketScopeIds: scopedIds,
      useForSegmentation: tempFieldData.useForSegmentation === true,
      options: (tempFieldData.fieldType === 'select' || tempFieldData.fieldType === 'checkbox') ? fieldOptions : undefined
    };
    
    if (currentFieldIndex !== null) {
      update(currentFieldIndex, fieldData);
    } else {
      append(fieldData);
    }
    
    handleCloseFieldDialog();
  };
  
  const handleFieldChange = (field: string, value: any) => {
    setTempFieldData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const ticketCatalogMode = watch('ticketCatalogMode');
  const salesPhases = watch('salesPhases');
  const tickets = watch('tickets');
  const ticketTypes = watch('ticketTypes');
  const registrationDeadline = watch('registrationDeadline');

  const deadlineContext = useMemo(
    () => ({ startDate, endDate, ticketCatalogMode, salesPhases, tickets, ticketTypes }),
    [startDate, endDate, ticketCatalogMode, salesPhases, tickets, ticketTypes]
  );

  const catalogTicketOptions = useMemo(
    () => getCatalogTicketsForFieldScope(ticketCatalogMode, tickets, ticketTypes),
    [ticketCatalogMode, tickets, ticketTypes]
  );

  const toggleTicketScope = (ticketId: string) => {
    setTempFieldData((prev) => {
      const current = prev.ticketScopeIds || [];
      const next = current.includes(ticketId)
        ? current.filter((id) => id !== ticketId)
        : [...current, ticketId];
      return { ...prev, ticketScopeIds: next };
    });
  };

  const canSaveField =
    Boolean(tempFieldData.fieldName.trim()) &&
    ((tempFieldData.fieldType !== 'select' && tempFieldData.fieldType !== 'checkbox') || fieldOptions.length > 0) &&
    (tempFieldData.appliesToAllTickets || tempFieldData.ticketScopeIds.length > 0);

  const eventDatesSummary = useMemo(() => getEventDatesSummary(deadlineContext), [deadlineContext]);
  const deadlinePresets = useMemo(() => buildDeadlinePresets(deadlineContext), [deadlineContext]);
  const recommendedDeadline = useMemo(() => getRecommendedDeadline(deadlineContext), [deadlineContext]);
  const lotScheduleWarning = useMemo(() => getLotScheduleWarning(deadlineContext), [deadlineContext]);

  const activePresetId = useMemo((): DeadlinePresetId | null => {
    if (!isValidRegistrationDeadline(registrationDeadline, startDate)) {
      return selectedPresetId;
    }
    const deadlineMs = new Date(registrationDeadline as Date).getTime();

    if (selectedPresetId) {
      const selected = deadlinePresets.find((p) => p.id === selectedPresetId);
      if (selected?.date && Math.abs(selected.date.getTime() - deadlineMs) < 60_000) {
        return selectedPresetId;
      }
    }

    const match = deadlinePresets.find(
      (p) => p.date && Math.abs(p.date.getTime() - deadlineMs) < 60_000
    );
    return match?.id ?? null;
  }, [registrationDeadline, startDate, deadlinePresets, selectedPresetId]);

  const hasInvalidDeadline = !isValidRegistrationDeadline(registrationDeadline, startDate);
  const usesPhasedOrBatched = ticketCatalogMode === 'phased' || ticketCatalogMode === 'batched';

  useEffect(() => {
    if (deadlineTouched || !recommendedDeadline.date) return;
    if (hasInvalidDeadline) {
      setValue('registrationDeadline', recommendedDeadline.date, { shouldValidate: true });
      if (recommendedDeadline.presetId) {
        setSelectedPresetId(recommendedDeadline.presetId);
      }
    }
  }, [deadlineTouched, hasInvalidDeadline, recommendedDeadline.date, recommendedDeadline.presetId, setValue]);

  const applyDeadlinePreset = (date: Date, presetId: DeadlinePresetId) => {
    setDeadlineTouched(true);
    setSelectedPresetId(presetId);
    setValue('registrationDeadline', date, { shouldValidate: true, shouldDirty: true });
  };

  const formCopy = useBrand().copy.eventForm;
  const t = useBrandRegistrationCopy();
  
  return (
    <Box>
      {!embedded && (
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        {formCopy.registrationSettingsTitle}
        <Tooltip title={formCopy.registrationSettingsHelp}>
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>
      )}
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          {formCopy.registrationDeadlineLabel}
        </Typography>

        {lotScheduleWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {lotScheduleWarning}
          </Alert>
        )}

        {eventDatesSummary && (
          <Alert severity="info" icon={<EventIcon fontSize="inherit" />} sx={{ mb: 2 }}>
            {eventDatesSummary}
            {usesPhasedOrBatched && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Com lotes, as vendas fecham pelo <strong>fim de cada lote</strong>. Use uma sugestão abaixo para alinhar este prazo sem precisar voltar na aba anterior.
              </Typography>
            )}
          </Alert>
        )}

        {deadlinePresets.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sugestões rápidas
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {deadlinePresets.map((preset) => {
                const isSelected = activePresetId === preset.id;
                const showRecommendedBadge = preset.recommended && !isSelected;
                return (
                  <Chip
                    key={preset.id}
                    label={
                      <Box>
                        <Typography component="span" variant="body2" fontWeight={isSelected ? 600 : 400}>
                          {preset.label}
                          {showRecommendedBadge ? ' ★' : ''}
                        </Typography>
                        <Typography component="span" variant="caption" display="block" sx={{ opacity: isSelected ? 0.95 : 0.85 }}>
                          {preset.hint}
                        </Typography>
                      </Box>
                    }
                    clickable
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => preset.date && applyDeadlinePreset(preset.date, preset.id)}
                    sx={{
                      height: 'auto',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      '& .MuiChip-label': { display: 'block', py: 0.75 },
                    }}
                  />
                );
              })}
            </Stack>
            {recommendedDeadline.reason && hasInvalidDeadline && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Sugestão automática: {recommendedDeadline.reason}
                {recommendedDeadline.date && ` (${formatDeadlineLabel(recommendedDeadline.date)})`}
              </Typography>
            )}
          </Box>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <Controller
                name="registrationDeadline"
                control={control}
                render={({ field }) => {
                  const parsed = field.value ? new Date(field.value) : null;
                  const isValid = isValidRegistrationDeadline(parsed, startDate);

                  return (
                    <DateTimePicker
                      label={formCopy.registrationDeadlineLabel}
                      value={isValid ? parsed : null}
                      maxDateTime={startDate ? new Date(startDate) : undefined}
                      onChange={(date) => {
                        setDeadlineTouched(true);
                        field.onChange(date);
                        if (date) {
                          const match = deadlinePresets.find(
                            (p) => p.date && Math.abs(p.date.getTime() - date.getTime()) < 60_000
                          );
                          setSelectedPresetId(match?.id ?? null);
                        } else {
                          setSelectedPresetId(null);
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.registrationDeadline,
                          helperText:
                            (errors.registrationDeadline?.message as string) ||
                            (startDate
                              ? `Deve ser antes do início do evento (${formatDeadlineLabel(startDate)})`
                              : 'Defina na aba Informações Básicas quando o evento começa'),
                        },
                      }}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Controller
              name="allowWaitlist"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Permitir Lista de Espera"
                  />
                  <FormHelperText>
                    Quando todas as vagas forem preenchidas, os interessados poderão entrar em uma lista de espera
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            Campos Personalizados
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpenFieldDialog()}
          >
            Adicionar Campo
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Adicione campos personalizados para coletar informações específicas dos participantes durante a {t('inscrição')}.
        </Typography>
        
        {fields.length === 0 ? (
          <Box sx={{ 
            p: 3, 
            textAlign: 'center', 
            border: '1px dashed #ccc',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum campo personalizado adicionado.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Os campos básicos (nome, email, telefone) são coletados automaticamente.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {fields.map((field, index) => {
              // Cast seguro para acessar propriedades personalizadas
              const customField = field as unknown as CustomField;
              return (
                <Grid item xs={12} key={field.id}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderLeft: customField.isRequired ? '4px solid #f44336' : '4px solid #9e9e9e'
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        {customField.fieldName}
                        {customField.isRequired && (
                          <Chip 
                            label="Obrigatório" 
                            size="small" 
                            color="error" 
                            sx={{ ml: 1, height: 20 }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tipo: {fieldTypes.find(t => t.value === customField.fieldType)?.label}
                        {customField.fieldType === 'select' && customField.options && (
                          <> • {customField.options.length} opções</>
                        )}
                      </Typography>
                      <Chip
                        label={getCustomFieldScopeLabel(customField, catalogTicketOptions)}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.75, height: 22 }}
                      />
                    </Box>
                    
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenFieldDialog(index)}
                        aria-label="Editar campo"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => remove(index)}
                        aria-label="Remover campo"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Opções Adicionais
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="allowGroupRegistration"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Permitir várias pessoas no mesmo pedido"
                />
              )}
            />
            <FormHelperText>
              Ativa o carrinho de ingressos: o comprador adiciona vários ingressos, preenche nome, e-mail, telefone e o formulário de cada pessoa, e paga tudo de uma vez. Cada participante recebe o próprio QR.
            </FormHelperText>
          </Grid>
          
          <Grid item xs={12}>
            <Controller
              name="requireApproval"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t('Exigir aprovação das inscrições')}
                />
              )}
            />
            <FormHelperText>
              {t('As inscrições ficarão pendentes até que sejam aprovadas pelo organizador')}
            </FormHelperText>
          </Grid>
          
          <Grid item xs={12}>
            <Controller
              name="sendConfirmationEmail"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Enviar email de confirmação"
                />
              )}
            />
            <FormHelperText>
              {t('Envia automaticamente um email de confirmação após a inscrição')}
            </FormHelperText>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Diálogo para adicionar/editar campo personalizado */}
      <Dialog 
        open={openFieldDialog} 
        onClose={handleCloseFieldDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {currentFieldIndex !== null ? 'Editar Campo' : 'Adicionar Campo'}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome do Campo"
                placeholder="Ex: Idade, Congregação, Restrição Alimentar"
                margin="normal"
                value={tempFieldData.fieldName}
                onChange={(e) => handleFieldChange('fieldName', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Tipo de Campo</InputLabel>
                <Select
                  value={tempFieldData.fieldType}
                  label="Tipo de Campo"
                  onChange={(e) => handleFieldChange('fieldType', e.target.value)}
                >
                  {fieldTypesByCategory.map((category) => [
                    <ListSubheader key={`header-${category.category}`} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      {category.category}
                    </ListSubheader>,
                    ...category.types.map((type) => (
                      <MenuItem 
                        key={type.value} 
                        value={type.value}
                        sx={{ pl: 3 }}
                      >
                        {type.label}
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={tempFieldData.isRequired}
                    onChange={(e) => handleFieldChange('isRequired', e.target.checked)}
                  />
                }
                label="Campo Obrigatório"
                sx={{ mt: 2 }}
              />
            </Grid>

            {AUDIENCE_FUNNEL_UI_ENABLED ? (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={tempFieldData.useForSegmentation}
                      onChange={(e) => handleFieldChange('useForSegmentation', e.target.checked)}
                    />
                  }
                  label="Usar para segmentar público"
                />
                <FormHelperText>
                  A resposta fica no perfil da pessoa em Novidades → Público / Filtros (próximos eventos).
                </FormHelperText>
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={tempFieldData.appliesToAllTickets}
                    onChange={(e) => handleFieldChange('appliesToAllTickets', e.target.checked)}
                  />
                }
                label="Aplicar a todos os tipos de ingresso"
              />
              <FormHelperText>
                Desative para mostrar esta pergunta só em ingressos específicos (ex.: almoço só no ingresso que inclui refeição).
              </FormHelperText>
            </Grid>

            {!tempFieldData.appliesToAllTickets && (
              <Grid item xs={12}>
                {catalogTicketOptions.length === 0 ? (
                  <Alert severity="info">
                    Configure os ingressos na etapa de pagamento antes de vincular esta pergunta.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Esta pergunta aparece em:
                    </Typography>
                    {catalogTicketOptions.map((option: TicketScopeOption) => (
                      <FormControlLabel
                        key={option.id}
                        control={
                          <Checkbox
                            checked={isScopeOptionSelected(tempFieldData.ticketScopeIds, option)}
                            onChange={() => toggleTicketScope(option.id)}
                          />
                        }
                        label={option.name}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
            )}
            
            {(tempFieldData.fieldType === 'select' || tempFieldData.fieldType === 'checkbox') && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  {tempFieldData.fieldType === 'select' ? 'Opções de Seleção' : 'Opções de Caixa de Seleção'}
                </Divider>
                
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Nova Opção"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleAddOption}
                    disabled={!newOption.trim()}
                    sx={{ ml: 1 }}
                  >
                    Adicionar
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {fieldOptions.map((option, index) => (
                    <Chip
                      key={index}
                      label={option}
                      onDelete={() => handleRemoveOption(index)}
                      size="medium"
                    />
                  ))}
                </Box>
                
                {fieldOptions.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {tempFieldData.fieldType === 'select' 
                      ? 'Adicione pelo menos uma opção para o campo de seleção'
                      : 'Adicione pelo menos uma opção para o campo de caixa de seleção'
                    }
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseFieldDialog}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveField}
            disabled={!canSaveField}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Dica: Campos personalizados bem planejados ajudam a coletar informações importantes para a organização do evento.
        </Typography>
      </Box>
    </Box>
  );
};

export default RegistrationForm;
